import _ from 'lodash';
import {
  AdminProductVariant,
  UrlImage,
  Tag,
  AdminCategory,
  AdminSpecItem,
  Video,
  LocalPropertyValue,
  LocaleResponse,
  LocalProperty,
  AdminProduct,
  AdminProductTypeEnum,
  FinanceProductId,
  Country,
} from '@cdaas/api-clients/dist/product-service-openapi';

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type TeliaFinanceGuidChangeParam = Optional<Pick<FinanceProductId, 'country_id' | 'finance_product_id'>, 'finance_product_id'>;

export interface ProductEditProps {
  id: number | undefined;
  type: AdminProductTypeEnum | undefined;
  handleTypeChange: (type: AdminProductTypeEnum) => void;
  brand: string | undefined;
  handleBrandChange: (brand: string) => void;
  name: string | undefined;
  handleNameChange: (name: string) => void;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  handleNameTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  handleVariantsTeliaFinanceGUIDChange: (financeProductId: TeliaFinanceGuidChangeParam) => void;
  variants: AdminProductVariant[] | undefined;
  handleVariantsChange: (variants: AdminProductVariant[]) => void;
  subCategories: number[] | undefined;
  handleSubCategoriesChange: (subCategories: number[]) => void;
  tags: number[] | undefined;
  handleTagsChange: (tags: number[]) => void;
  images: UrlImage[] | undefined;
  handleImagesChange: (images: UrlImage[]) => void;
  translationsMetadata: { id: number; localeId: number }[] | undefined;
  keySellingPoints: string[] | undefined;
  handleKeySellingPointsChange: (tags: string[]) => void;
  keySellingPointsTranslations: { localeId: number; translations: string[] }[] | undefined;
  handleKeySellingPointsTranslationsChange: (translations: { localeId: number; translations: string[] }[]) => void;
  description: string | undefined;
  handleDescriptionChange: (description: string) => void;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  handleDescriptionTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  boxContents: string[] | undefined;
  handleBoxContentsChange: (tags: string[]) => void;
  boxContentsTranslations: { localeId: number; translations: string[] }[] | undefined;
  handleBoxContentsTranslationsChange: (translations: { localeId: number; translations: string[] }[]) => void;
  specs: AdminSpecItem[] | undefined;
  handleSpecsChange: (specs: AdminSpecItem[]) => void;
  videos: Video[] | undefined;
  handleVideosChange: (videos: Video[]) => void;

  properties: LocalProperty[] | undefined;
  propertyValues: LocalPropertyValue[] | undefined;
  handleAddProperty: (property: LocalProperty) => void;
  handleRemoveProperty: (property: LocalProperty) => void;

  allTagsInSystem: Tag[];
  allPropertiesInSystem: LocalProperty[];
  allCategoriesInSystem: AdminCategory[];

  currentLocale: LocaleResponse;
  locales: LocaleResponse[];
  handleLocaleChange: (localeId: number) => void;

  variationsExist: boolean;
  addMode: boolean;
  countries: Country[];
}

export interface Errors {
  name?: string;
  sku?: string;
  brand?: string;
  category?: string;
  variants?: {
    variantsSKUs?: { index: number; error: string }[];
    variantsPropertyValues?: { index: number; errors: { propertyId: number; error: string }[] }[];
    variantsDuplicates?: { index: number; error: string }[];
  };
  boxContents?: {
    boxContentsGeneral?: string;
    boxContents?: { index: number; error: string }[];
  };
  keySellingPoints?: {
    keySellingPointsGeneral?: string;
    keySellingPoints?: { index: number; error: string }[];
  };
  description?: {
    description?: string;
    descriptionTranslation?: string;
  };
  specs?: { index: number; error?: string; lines?: { index: number; error: string }[] }[];
  videos?: string;
  financeId?: string;
}

interface validateParams {
  name: string | undefined;
  brand: string | undefined;
  variants: AdminProductVariant[] | undefined;
  subCategories: number[] | undefined;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  boxContents: string[] | undefined;
  keySellingPoints: string[] | undefined;
  description: string | undefined;
  specs: AdminSpecItem[] | undefined;
  videos: Video[] | undefined;
  properties: LocalProperty[] | undefined;
  locales: LocaleResponse[];
  allPropertiesInSystem: LocalProperty[];
  t: (T: string, params?: { languages?: string }) => string;
}

export const validate = (params: validateParams): { valid: boolean; errors: Errors } => {
  const {
    name,
    brand,
    variants,
    subCategories,
    keySellingPoints,
    description,
    descriptionTranslations,
    boxContents,
    specs,
    videos,
    properties,
    allPropertiesInSystem,
    locales,
    t,
  } = params;
  const newErrors: Errors = {};

  const getLanguage = (localeId: number | undefined) => {
    const locale = locales.find(locale => locale.id === localeId);
    if (locale) {
      return locale.name;
    }
  };

  // Name, SKU, category
  if (name === '') {
    newErrors.name = t('editscreen.errors.name_blank');
  }
  if (brand === '') {
    newErrors.brand = t('editscreen.errors.brand_blank');
  }
  if (variants && variants[0].sku === '') {
    newErrors.sku = t('editscreen.errors.sku_blank');
  }
  if (!subCategories?.[0]) {
    newErrors.category = t('editscreen.errors.category_empty');
  }
  // Variants errors = Variant SKUs, property values and duplicates
  const variantsErrors: Errors['variants'] = {};
  variants?.forEach((variant, index) => {
    // SKUs
    let error;
    if (variant.sku === '') {
      error = { index, error: t('editscreen.errors.sku_blank') };
    } else if ((variants || []).filter(someVariant => someVariant.sku === variant.sku).length > 1) {
      error = { index, error: t('editscreen.errors.sku_duplicate') };
    }
    if (error) {
      if (variantsErrors.variantsSKUs) variantsErrors.variantsSKUs.push(error);
      else variantsErrors.variantsSKUs = [error];
    }
    // Property values
    const propertiesWithoutValue: { propertyId: number; error: string }[] = [];
    (properties || []).forEach(property => {
      const propertyWithAllValues = allPropertiesInSystem.find(propertyWithValues => propertyWithValues.id === property.id);
      const allValuesOfProperty = propertyWithAllValues?.property_values?.map(propertyValue => propertyValue.id) || [];
      if (!variant.property_value_ids || !variant.property_value_ids.some(propertyValueId => allValuesOfProperty.includes(propertyValueId))) {
        propertiesWithoutValue.push({
          propertyId: property.id,
          error: t('editscreen.errors.property_value_empty'),
        });
      }
    });
    if (propertiesWithoutValue.length !== 0) {
      const error = { index, errors: propertiesWithoutValue.map(propertyWithoutValue => ({ ...propertyWithoutValue })) };
      if (variantsErrors.variantsPropertyValues) variantsErrors.variantsPropertyValues.push(error);
      else variantsErrors.variantsPropertyValues = [error];
    }
    // Duplicates (same combination of property values on multiple variants)
    const variantsPropertyValueCombinations = variants?.map(variant => variant.property_value_ids);
    const duplicateFound =
      (variantsPropertyValueCombinations || []).filter(
        propertyValueCombination => JSON.stringify(propertyValueCombination) === JSON.stringify(variant.property_value_ids),
      ).length > 1;
    if (duplicateFound) {
      const error = { index, error: t('editscreen.errors.variant_duplicate') };
      if (variantsErrors.variantsDuplicates) variantsErrors.variantsDuplicates.push(error);
      else variantsErrors.variantsDuplicates = [error];
    }
  });
  if (!_.isEmpty(variantsErrors)) newErrors.variants = variantsErrors;
  // Box contents
  const boxContentsErrors: Errors['boxContents'] = {};
  if (!boxContents || boxContents.length === 0) {
    boxContentsErrors.boxContentsGeneral = t('editscreen.errors.box_contents_none');
  }
  boxContents?.forEach((boxContent, index) => {
    if (boxContent === '') {
      const error = { index, error: t('editscreen.errors.box_content_blank') };
      if (boxContentsErrors.boxContents) boxContentsErrors.boxContents.push(error);
      else boxContentsErrors.boxContents = [error];
    }
  });
  if (!_.isEmpty(boxContentsErrors)) newErrors.boxContents = boxContentsErrors;
  // Key selling points
  const keySellingPointsErrors: Errors['keySellingPoints'] = {};
  if (!keySellingPoints || keySellingPoints.length === 0) {
    keySellingPointsErrors.keySellingPointsGeneral = t('editscreen.errors.key_selling_points_none');
  }
  keySellingPoints?.forEach((keySellingPoint, index) => {
    if (keySellingPoint === '') {
      const error = { index, error: t('editscreen.errors.key_selling_point_blank') };
      if (keySellingPointsErrors.keySellingPoints) keySellingPointsErrors.keySellingPoints.push(error);
      else keySellingPointsErrors.keySellingPoints = [error];
    }
  });
  if (!_.isEmpty(keySellingPointsErrors)) newErrors.keySellingPoints = keySellingPointsErrors;
  // Description
  const descriptionErrors: Errors['description'] = {};
  if (description === '') {
    descriptionErrors.description = t('editscreen.errors.description_blank');
  }
  // Description translation
  const descriptionBlankTranslations: string[] = [];
  locales.forEach(locale => {
    const translation = descriptionTranslations?.find(translation => translation.localeId === locale.id);
    if (!translation || !translation.translation) {
      descriptionBlankTranslations.push(locale.name);
    }
  });
  if (descriptionBlankTranslations.length !== 0) {
    descriptionErrors.descriptionTranslation = t('editscreen.errors.description_translation_blank', {
      languages: descriptionBlankTranslations.join(', '),
    });
  }
  if (!_.isEmpty(descriptionErrors)) newErrors.description = descriptionErrors;
  // Specs
  specs?.forEach((spec, index) => {
    let blankLabelError;
    let noLinesError;
    const blankLineErrors: { index: number; error: string }[] = [];
    if (spec.name === '') {
      blankLabelError = t('editscreen.errors.spec_label_blank');
    }
    if (spec.lines.length === 0) {
      noLinesError = t('editscreen.errors.spec_no_lines');
    }
    spec.lines.forEach((line, lineIndex) => {
      if (line === '') {
        blankLineErrors.push({ index: lineIndex, error: t('editscreen.errors.spec_line_blank') });
      }
    });
    if (blankLabelError || noLinesError || blankLineErrors.length !== 0) {
      const error = {
        index,
        ...((blankLabelError || noLinesError) && { error: blankLabelError ?? noLinesError }),
        ...(blankLineErrors.length !== 0 && { lines: blankLineErrors }),
      };
      if (newErrors.specs) newErrors.specs.push(error);
      else newErrors.specs = [error];
    }
  });
  // Videos
  const blankLanguages: string[] = [];
  const invalidUrlLanguages: string[] = [];
  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };
  videos?.forEach(video => {
    const language = getLanguage(video.locale_id);
    if (video.url === '') {
      if (language && blankLanguages.indexOf(language) === -1) {
        blankLanguages.push(language);
      }
    }
    if (!validateUrl(video.url)) {
      if (language && invalidUrlLanguages.indexOf(language) === -1) {
        invalidUrlLanguages.push(language);
      }
    }
  });
  if (blankLanguages.length !== 0) {
    newErrors.videos = t('editscreen.errors.some_videos_blank', { languages: blankLanguages.join(', ') });
  } else if (invalidUrlLanguages.length !== 0) {
    newErrors.videos = t('editscreen.errors.some_videos_invalid_url', { languages: invalidUrlLanguages.join(', ') });
  }

  const valid = _.isEmpty(newErrors);
  return { valid, errors: newErrors };
};

export const ensureEditableItemInputChangesSavedOnSaveBtnClick = async (saveChangesBtnId: string) => {
  const element = document.getElementById(saveChangesBtnId);
  element?.focus();
  return new Promise(resolve => setTimeout(resolve, 0));
};

interface createBackendProductParams {
  id: number | undefined;
  type: AdminProductTypeEnum | undefined;
  brand: string | undefined;
  name: string | undefined;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  variants: AdminProductVariant[] | undefined;
  subCategories: number[] | undefined;
  tags: number[] | undefined;
  images: UrlImage[] | undefined;
  translationsMetadata: { id: number; localeId: number }[] | undefined;
  keySellingPoints: string[] | undefined;
  keySellingPointsTranslations: { localeId: number; translations: string[] }[] | undefined;
  description: string | undefined;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  boxContents: string[] | undefined;
  boxContentsTranslations: { localeId: number; translations: string[] }[] | undefined;
  specs: AdminSpecItem[] | undefined;
  videos: Video[] | undefined;
  properties: LocalProperty[] | undefined;
}

export const createBackendProduct = (params: createBackendProductParams): AdminProduct => {
  const {
    id,
    type,
    brand,
    name,
    nameTranslations,
    variants,
    subCategories,
    tags,
    images,
    translationsMetadata,
    keySellingPoints,
    keySellingPointsTranslations,
    description,
    descriptionTranslations,
    boxContents,
    boxContentsTranslations,
    specs,
    videos,
    properties,
  } = params;

  // In case a spec translation has no content (i.e. no translation for spec name or any lines of the spec)
  // -> remove the translation from the spec
  const specsWithoutEmptyTranslations = specs
    ? specs.map(spec => {
        const filteredTranslations = (spec.translations || []).filter(translation => {
          return translation.name || translation.lines.find(lineTranslation => lineTranslation);
        });
        return {
          ...spec,
          translations: filteredTranslations,
        };
      })
    : undefined;

  const translations = (translationsMetadata || [])
    // Create translation objects to be sent to backend
    // In case some field in translation object has only empty string -> do not send the empty string to backend
    .map(translationMetadata => {
      const name = nameTranslations?.find(t => t.localeId === translationMetadata.localeId && t.translation)?.translation;
      const description = descriptionTranslations?.find(t => t.localeId === translationMetadata.localeId && t.translation)?.translation;

      const tempKspTranslations = keySellingPointsTranslations?.find(t => t.localeId === translationMetadata.localeId)?.translations;
      const kspTranslations = tempKspTranslations?.find(t => t !== '') ? tempKspTranslations : [];
      const tempBoxContentTranslations = boxContentsTranslations?.find(t => t.localeId === translationMetadata.localeId)?.translations;
      const boxContentTranslations = tempBoxContentTranslations?.find(t => t !== '') ? tempBoxContentTranslations : [];

      return {
        id: translationMetadata.id,
        locale_id: translationMetadata.localeId,
        name,
        key_selling_points: kspTranslations,
        description,
        box_content: boxContentTranslations,
      };
    })
    // In case some translation has no content -> remove the whole translation
    // Currently each product translation should have description at least (validation checks this)
    // but in case this changes in future translation(s) with no content should be removed
    .filter(translation => {
      return (
        translation.name ||
        translation.key_selling_points.find(kspTranslation => kspTranslation) ||
        translation.description ||
        translation.box_content.find(boxContentTranslation => boxContentTranslation)
      );
    });

  // Backend expects each image to have unique value in position field. Make sure this is the case.
  (images || []).forEach((image, index) => {
    image.position = index;
  });

  return {
    id: id || 0,
    type: type || AdminProductTypeEnum.Hardware,
    brand: brand || '',
    name: name || '',
    variants: variants || [],
    subcategory_ids: subCategories || [],
    tag_ids: tags,
    images,
    translations,
    box_content: boxContents || [],
    key_selling_points: keySellingPoints || [],
    description: description || '',
    main_specs: specsWithoutEmptyTranslations,
    videos,
    property_ids: properties?.map(property => property.id),
  };
};
